/**
 * CETUS UserList / 공개 프로필 → 전문가/기관 탐색 카드·필터용 모델
 * 회원가입 신청서(개인: individual_user_info 등)와 맞춘 라벨·표시 규칙
 */

import { buildExpertDirectoryBaseMeta, truncateText } from './expertDirectoryCardMeta';
import { PROFILE_ROW_LABEL, fieldLabelKo, resolveLabelForFieldKey } from './expertDirectoryFieldLabels';
import { MEMBER_KIND_CHIP, memberTypeDisplayName } from './memberDirectoryApi';

export { MEMBER_KIND_CHIP };

export {
  buildExpertDirectoryBaseMeta,
  truncateText,
  fieldLabelKo,
  resolveLabelForFieldKey,
};

/** UserList 행에 붙은 JSON(metaData 등)을 객체로 */
function parseFormBlob(u) {
  const raw = u.metaData ?? u.meta_data;
  if (raw == null) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return {};
    }
  }
  return {};
}

export function profileImageUrlFromProfileId(profileId) {
  if (profileId == null || profileId === '') return null;
  const str = String(profileId).trim();
  if (!str) return null;
  return Number.isNaN(Number(str))
    ? `/api/glow/files/view?fileId=${encodeURIComponent(str)}`
    : `/api/glow/files/viewByUid/${str}`;
}

/** CETUS 프로필 — profileUid 우선, 없으면 profileId(파일 키) 기반 URL (AuthorDisplay와 동일 규칙) */
export function resolveProfileImageUrl(profileUid, profileId) {
  if (profileUid != null && String(profileUid).trim() !== '') {
    return `/api/glow/files/viewByUid/${encodeURIComponent(String(profileUid).trim())}`;
  }
  return profileImageUrlFromProfileId(profileId);
}

/**
 * 신청서(meta_data) 기업·기관 로고 등 → 목록 썸네일 URL (CETUS 프로필 이미지가 없을 때)
 * MyInfo.jsx company_logo 파싱과 동일한 규칙
 */
export function resolveFormLogoThumbUrl(form) {
  if (!form || typeof form !== 'object') return null;
  const keys = [
    'company_logo',
    'companyLogo',
    'org_logo',
    'organization_logo',
    'institution_logo',
    'region_logo',
    'logo',
  ];
  let logoValue = null;
  for (const k of keys) {
    const v = form[k];
    if (v != null && v !== '') {
      logoValue = v;
      break;
    }
  }
  const details = form.details;
  if (logoValue == null && details && typeof details === 'object') {
    for (const k of keys) {
      const v = details[k];
      if (v != null && v !== '') {
        logoValue = v;
        break;
      }
    }
  }
  if (logoValue == null) return null;

  let extractedUid = logoValue;
  if (typeof logoValue === 'string' && logoValue.startsWith('[')) {
    try {
      const arr = JSON.parse(logoValue);
      if (arr && arr.length > 0) extractedUid = arr[0].fileId || arr[0].uid || arr[0];
    } catch (_) {
      /* ignore */
    }
  } else if (Array.isArray(logoValue) && logoValue.length > 0) {
    extractedUid = logoValue[0].fileId || logoValue[0].uid || logoValue[0];
  } else if (typeof logoValue === 'object' && logoValue !== null) {
    extractedUid = logoValue.fileId || logoValue.uid || extractedUid;
  }

  const strVal = String(extractedUid).trim();
  if (!strVal) return null;
  if (strVal.startsWith('data:') || strVal.startsWith('http') || strVal.startsWith('/')) {
    return strVal;
  }
  if (Number.isNaN(Number(strVal))) {
    return `/api/glow/files/view?fileId=${encodeURIComponent(strVal)}`;
  }
  return `/api/glow/files/viewByUid/${encodeURIComponent(strVal)}`;
}

/** details.* 중첩에서 문자열 값 추출 (가입 신청서 JSON 구조 대응) */
export function pickDetailsString(formData, keys) {
  if (!formData || typeof formData !== 'object') return '';
  const details = formData.details;
  if (details && typeof details === 'object') {
    for (const k of keys) {
      const v = details[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  for (const k of keys) {
    const v = formData[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** 기관 ob_company_org_info.partnership JSON (수요·협력 정보) */
function pickPartnershipString(formData, keys) {
  const p = formData?.partnership;
  if (!p || typeof p !== 'object') return '';
  for (const k of keys) {
    const v = p[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** partnership 내 여러 값을 한 줄로 (목록 요약용) */
function joinPartnershipPreview(formData, keys) {
  const p = formData?.partnership;
  if (!p || typeof p !== 'object') return '';
  const parts = [];
  for (const k of keys) {
    const v = p[k];
    if (v != null && String(v).trim() !== '') parts.push(String(v).trim());
  }
  return parts.join(' · ');
}

function hasMeaningfulValue(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

/** 카드 메타 행 순서 — search_expert_org.search_data(JSON) 키 기준 */
const ORG_SEARCH_DATA_KEY_ORDER = [
  'main_industry',
  'technical_field',
  'organization_name',
  'company_name',
  'companyName',
  'org_name',
  'affiliation',
  'address',
  'preferences_location',
  'certifications',
  'major_projects_papers_achievements',
  'major projects papers achievements',
];

const IND_SEARCH_DATA_KEY_ORDER = [
  'preferences_fields',
  'interests',
  'affiliation',
  'university',
  'school_name',
  'schoolName',
  'major',
  'department',
  'preferences_location',
  'certifications',
  'major_projects_papers_achievements',
  'major projects papers achievements',
  'bio',
  'sns',
];

/**
 * meta_data(= search_data)에 값이 있는 필드 키만 수집 — details·partnership 하위 키는 평탄화
 * @param {object} form
 * @returns {string[]}
 */
export function collectSearchDataKeysFromForm(form) {
  if (!form || typeof form !== 'object' || Array.isArray(form)) return [];
  const seen = new Set();
  const keys = [];
  const addKey = (k) => {
    const t = String(k).trim();
    if (!t) return;
    const low = t.toLowerCase();
    if (seen.has(low)) return;
    seen.add(low);
    keys.push(t);
  };
  for (const [k, v] of Object.entries(form)) {
    if (k === 'details' && v && typeof v === 'object' && !Array.isArray(v)) {
      for (const dk of Object.keys(v)) {
        if (hasMeaningfulValue(v[dk])) addKey(dk);
      }
      continue;
    }
    if (k === 'partnership' && v && typeof v === 'object' && !Array.isArray(v)) {
      for (const pk of Object.keys(v)) {
        if (hasMeaningfulValue(v[pk])) addKey(pk);
      }
      continue;
    }
    if (hasMeaningfulValue(v)) addKey(k);
  }
  return keys;
}

function sortSearchDataKeysByOrder(keys, order) {
  const rank = (k) => {
    const low = String(k).toLowerCase();
    const i = order.findIndex((o) => o.toLowerCase() === low);
    return i === -1 ? 1000 : i;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b)));
}

/**
 * 목록 카드: search_data에 존재하는 필드만 { key, value } (표시 순서 고정)
 */
function buildSearchDataEntriesForCard(form, isIndividualDirectoryRow) {
  const rawKeys = collectSearchDataKeysFromForm(form);
  if (rawKeys.length === 0) return [];
  const order = isIndividualDirectoryRow ? IND_SEARCH_DATA_KEY_ORDER : ORG_SEARCH_DATA_KEY_ORDER;
  const sorted = sortSearchDataKeysByOrder(rawKeys, order);
  return sorted
    .map((k) => {
      const value = pickDetailsString(form, [k]);
      return { key: k, value: value != null ? String(value).trim() : '' };
    })
    .filter((e) => e.value);
}

/** Register / memberDirectoryApi.authorCdFromMemberTypeDefinition 과 동일 규칙 */
function findMemberTypeDefinitionByAuthorCd(definitions, authorCd) {
  if (!Array.isArray(definitions) || authorCd == null || String(authorCd).trim() === '') return null;
  const target = String(authorCd).trim().toUpperCase();
  return (
    definitions.find((d) => {
      const cd = d?.displayNmEn
        ? String(d.displayNmEn).trim().toUpperCase()
        : String(d.objectNmEn || '').trim().toUpperCase();
      return cd === target;
    }) ?? null
  );
}

/** authorCd(CETUS_USER) = 정의의 displayNmEn(또는 objectNmEn) 일 때 화면 표시명 */
export function displayNmForAuthorCd(definitions, authorCd, lang) {
  const def = findMemberTypeDefinitionByAuthorCd(definitions, authorCd);
  if (!def) return '';
  return memberTypeDisplayName(def, lang);
}

/** 레이아웃 템플릿 objectKey — MyInfo 의 userObjectKey 와 동일 규칙 */
export function resolveMemberObjectKeyFromAuthorCd(authorCd, memberTypeDefinitions = []) {
  const def = findMemberTypeDefinitionByAuthorCd(memberTypeDefinitions, authorCd);
  const key = def?.objectNmEn;
  return key != null && String(key).trim() !== '' ? String(key).trim() : '';
}

/**
 * 공개 프로필 상세 API(userInfo + formData) → 목록 카드와 동일한 mapUserListToExpertCardItem 결과
 */
export function expertCardItemFromPublicProfile(userInfo, formData, opts = {}) {
  if (!userInfo || typeof userInfo !== 'object') return null;
  const authorCd = String(userInfo.authorCd ?? userInfo.author_cd ?? '').toUpperCase();
  const metaPayload = formData && typeof formData === 'object' ? formData : {};
  const row = {
    uid: userInfo.uid ?? userInfo.UID,
    userNm: userInfo.userNm ?? userInfo.user_nm ?? '',
    userEmail: userInfo.userEmail ?? userInfo.user_email ?? '',
    userTel: userInfo.userTel ?? userInfo.user_tel ?? '',
    roleCd: authorCd,
    roleNm: userInfo.authorNm ?? userInfo.author_nm ?? '',
    role_cd: authorCd,
    profileId: userInfo.profileId ?? userInfo.profile_id,
    profileUid: userInfo.profileUid ?? userInfo.profile_uid,
    directoryEntryKind: userInfo.directoryEntryKind ?? userInfo.directory_entry_kind ?? 'USER',
    metaData: JSON.stringify(metaPayload),
  };
  return mapUserListToExpertCardItem(row, opts);
}

/** Register.jsx isCompany 와 동일 — 기업/기관 전용 objectNmEn */
function memberKindFromFmsObjectNmEn(objectNmEn) {
  if (!objectNmEn) return null;
  const low = String(objectNmEn).toLowerCase();
  if (low === 'company_user_info' || low === 'government_user_info' || low === 'institution_user_info') {
    return MEMBER_KIND_CHIP.ORG;
  }
  return MEMBER_KIND_CHIP.INDIVIDUAL;
}

/**
 * @param {object} u - UserList API 행 (CETUS_USER.AUTHOR_CD = roleCd)
 * @param {{ authorCdToFmsDisplay?: Record<string, string>, memberTypeDefinitions?: Array<object> }} [opts]
 *        FMS 회원 유형 정의(검색 폼과 동일) — CETUS AUTHOR_CD 로 매칭해 표시명·칩 구분
 */
export function mapUserListToExpertCardItem(u, opts = {}) {
  const { authorCdToFmsDisplay = {}, memberTypeDefinitions = [], t = null, lang } = opts;
  const directoryEntryKind =
    u.directoryEntryKind ?? u.directory_entry_kind ?? u.DIRECTORY_ENTRY_KIND ?? 'USER';
  /** 디렉터리 개인 행 — AUTHOR_CD 가 INDIVIDUAL 일 필요 없음(STUDENT, PROFESSOR 등 individualUserInfo 전부) */
  const isIndividualDirectoryRow = String(directoryEntryKind).toUpperCase() === 'USER';
  const uid = u.uid ?? u.UID;
  const userNm = u.userNm ?? u.user_nm ?? '';
  const userEmail = u.userEmail ?? u.user_email ?? '';
  const userTel = u.userTel ?? u.user_tel ?? '';
  const roleCd = String(u.roleCd ?? u.role_cd ?? '').toUpperCase();
  const roleNm = u.roleNm ?? u.role_nm ?? '';

  const fmsLabel = roleCd ? authorCdToFmsDisplay[roleCd] : '';
  const matchedDef = findMemberTypeDefinitionByAuthorCd(memberTypeDefinitions, roleCd);
  const matchedDisplay = matchedDef ? memberTypeDisplayName(matchedDef, lang) : '';

  /** role_nm(CETUS_AUTHOR_INFO) 미사용 — FMS 회원 유형 displayNmEn = roleCd 일 때 displayNm */
  let typeLabel = fmsLabel || matchedDisplay || roleNm || (t ? t('experts.common.memberDefault') : '회원');
  if (!fmsLabel && !matchedDef) {
    if (isIndividualDirectoryRow) typeLabel = t ? t('experts.common.expert') : '전문가';
    else if (roleCd === 'COMPANY') typeLabel = t ? t('experts.common.company') : '기업';
    else if (roleCd === 'ORGANIZATION') typeLabel = t ? t('experts.common.organization') : '기관';
  }

  let memberKind = isIndividualDirectoryRow ? MEMBER_KIND_CHIP.INDIVIDUAL : MEMBER_KIND_CHIP.ORG;
  const kindFromFms = matchedDef?.objectNmEn != null ? memberKindFromFmsObjectNmEn(matchedDef.objectNmEn) : null;
  if (kindFromFms != null) {
    memberKind = kindFromFms;
  }

  const profileUid = u.profileUid ?? u.profile_uid;
  const profileId = u.profileId ?? u.profile_id;
  const form = parseFormBlob(u);
  const thumbFromProfile = resolveProfileImageUrl(profileUid, profileId);
  const thumbFromFormLogo = thumbFromProfile ? null : resolveFormLogoThumbUrl(form);
  const thumb = thumbFromProfile || thumbFromFormLogo;
  const workplaceNm = u.workplaceNm ?? u.workplace_nm ?? '';
  const deptNm = u.deptNm ?? u.dept_nm ?? '';

  /** meta_data = search_expert_org.search_data — 유형별 키 차이(예: university) 보조 */
  const affiliation = pickDetailsString(form, ['affiliation', 'university']);
  const schoolName = pickDetailsString(form, ['school_name', 'schoolName']);
  const department = pickDetailsString(form, ['department']);
  const major = pickDetailsString(form, ['major']);
  const interests = pickDetailsString(form, ['interests']);
  const preferencesFields = pickDetailsString(form, ['preferences_fields']);
  const preferencesLocation = pickDetailsString(form, ['preferences_location']);
  const certifications = pickDetailsString(form, ['certifications']);
  const majorProjectsPapers = pickDetailsString(form, [
    'major projects papers achievements',
    'major_projects_papers_achievements',
  ]);
  const mainIndustry = pickDetailsString(form, ['main_industry']);
  const technicalField = pickDetailsString(form, ['technical_field']);
  const orgAddress = pickDetailsString(form, ['address']);
  const orgName =
    pickDetailsString(form, ['company_name', 'companyName', 'organization_name', 'org_name']) || '';

  let field = '';
  let region = '';
  let expertise = '';
  let affiliationDisplay = '';
  let interestsPreview = '';
  let preferencesFieldsOut = preferencesFields;

  if (isIndividualDirectoryRow) {
    field = preferencesFields || interests.split(/[,，]/)[0]?.trim() || major || '';
    region = preferencesLocation;
    expertise = certifications || interests || '';
    /** 카드·요약: 신청서 `affiliation`(소속)만 — 학교명·학과·직장명은 검색용 searchBlob 에만 포함 */
    affiliationDisplay = affiliation || '';
    interestsPreview = interests;
  } else if (roleCd === 'COMPANY') {
    /** 기업: 카드/요약은 ob_company_org_info — 주요 산업군(main_industry) + 세부 전문기술(technical_field) */
    field = technicalField || '';
    region = orgAddress || preferencesLocation;
    expertise =
      joinPartnershipPreview(form, ['rdNeeds', 'requiredManpower', 'cooperationType', 'collaborationExperience']) ||
      certifications ||
      interests ||
      '';
    /** search_data에 기관명 키가 없으면 CETUS userNm으로 채우지 않음(목록 메타·요약과 search_data 일치) */
    affiliationDisplay = orgName || '';
    interestsPreview = [mainIndustry, technicalField].filter(Boolean).join(' · ') || '';
  } else {
    field =
      [technicalField, mainIndustry].filter(Boolean).join(' · ') ||
      preferencesFields ||
      interests.split(/[,，]/)[0]?.trim() ||
      '';
    region = orgAddress || preferencesLocation;
    expertise =
      joinPartnershipPreview(form, ['rdNeeds', 'requiredManpower', 'cooperationType', 'collaborationExperience']) ||
      certifications ||
      interests ||
      '';
    affiliationDisplay = orgName || '';
    interestsPreview = interests || technicalField || mainIndustry || '';
  }

  const regionSummaryLabel =
    roleCd === 'INDIVIDUAL'
      ? (t ? t('experts.summary.regionPreferred') : '희망 참여')
      : (t ? t('experts.summary.regionLocation') : '소재지');

  const fieldSummaryLine = (() => {
    if (isIndividualDirectoryRow) {
      return field ? `${t ? t('experts.summary.interestField') : '관심 분야'} ${field}` : '';
    }
    if (roleCd === 'COMPANY' || roleCd === 'ORGANIZATION') {
      const parts = [];
      if (mainIndustry && String(mainIndustry).trim()) {
        parts.push(`${t ? t('experts.summary.mainIndustry') : '주요 산업군'} ${String(mainIndustry).trim()}`);
      }
      if (technicalField && String(technicalField).trim()) {
        parts.push(`${t ? t('experts.summary.technicalField') : '세부 전문기술 분야'} ${String(technicalField).trim()}`);
      }
      if (parts.length) return parts.join(' · ');
    }
    return field ? `${t ? t('experts.summary.techField') : '기술 분야'} ${field}` : '';
  })();

  const listSummary = truncateText(
    [
      fieldSummaryLine,
      affiliationDisplay && `${t ? t('experts.summary.affiliation') : '소속'} ${affiliationDisplay}`,
      region && `${regionSummaryLabel} ${region}`,
      isIndividualDirectoryRow &&
        certifications &&
        `${t ? t('experts.summary.certifications') : '보유기술 및 자격증'} ${truncateText(certifications, 50)}`,
      isIndividualDirectoryRow &&
        majorProjectsPapers &&
        `${t ? t('experts.summary.majorProjects') : '주요 프로젝트·논문·성과'} ${truncateText(majorProjectsPapers, 50)}`,
      !isIndividualDirectoryRow && expertise && `${t ? t('experts.summary.capability') : '역량'} ${truncateText(expertise, 80)}`,
      roleCd === 'INDIVIDUAL' ? pickDetailsString(form, ['bio']) : pickPartnershipString(form, ['rdNeeds']),
    ]
      .filter(Boolean)
      .join(' · ') ||
      [userNm, ...(isIndividualDirectoryRow ? [userEmail] : [])].filter(Boolean).join(' · '),
    220
  );

  const searchBlob = [
    userNm,
    ...(isIndividualDirectoryRow ? [userEmail] : []),
    userTel,
    field,
    region,
    expertise,
    affiliationDisplay,
    isIndividualDirectoryRow ? schoolName : '',
    isIndividualDirectoryRow ? department : '',
    isIndividualDirectoryRow ? workplaceNm : '',
    isIndividualDirectoryRow ? deptNm : '',
    preferencesFields,
    interests,
    orgName,
    mainIndustry,
    technicalField,
    majorProjectsPapers,
    joinPartnershipPreview(form, ['rdNeeds', 'requiredManpower']),
  ]
    .filter(Boolean)
    .join(' ');

  const directoryLikeCountRaw = u.directoryLikeCount ?? u.directory_like_count;
  const directoryLikeCount =
    directoryLikeCountRaw != null && Number.isFinite(Number(directoryLikeCountRaw))
      ? Number(directoryLikeCountRaw)
      : 0;
  const directoryLikedByMe = Boolean(u.directoryLikedByMe ?? u.directory_liked_by_me);

  return {
    id: uid,
    uid,
    directoryEntryKind,
    name: userNm,
    title: userNm,
    type: typeLabel,
    memberKind,
    roleCd,
    roleNm: fmsLabel || roleNm,
    email: isIndividualDirectoryRow ? userEmail : '',
    userTel,
    contact: userTel || userNm,
    field: field || '',
    mainIndustry: mainIndustry || '',
    technicalField: technicalField || '',
    region: region || '',
    expertise: expertise || '',
    affiliation: affiliationDisplay || '',
    orgName: orgName || '',
    schoolName: schoolName || '',
    preferencesFields: preferencesFieldsOut || '',
    interestsPreview: interestsPreview || '',
    interestsLine: interests || '',
    certificationsCard: certifications || '',
    majorProjectsCard: majorProjectsPapers || '',
    projectCount: null,
    thumb: thumb || undefined,
    desc: listSummary,
    listSummary,
    org: affiliationDisplay || orgName || '',
    proposalStatus: null,
    proposedDemands: [],
    _searchBlob: searchBlob,
    /** search_data(JSON)에 실제로 있는 필드만 — 카드 메타는 이 배열 우선 */
    _searchDataEntries: buildSearchDataEntriesForCard(form, isIndividualDirectoryRow),
    directoryLikeCount,
    directoryLikedByMe,
  };
}

/**
 * 상세 페이지용: 공개 프로필 API 응답(userInfo + formData)에서 메타 행 생성
 * @param {Array<{ displayNmEn?: string, objectNmEn?: string, displayNm?: string, objectNm?: string }>} [memberTypeDefinitions] — 있으면 authorCd로 displayNm 매핑(백엔드 authorNm 불필요)
 * @param {Record<string, string>} [fmsLabelMap] — fetchMergedFmsFieldLabelMap() 결과(필드키→FMS 표시명)
 */
export function buildProfileMetaRows(userInfo, formData, memberTypeDefinitions = [], fmsLabelMap = {}, opts = {}) {
  const L = fmsLabelMap || {};
  const profileRowLabels = opts.profileRowLabels || PROFILE_ROW_LABEL;
  const t = opts.t || null;
  const rows = [];
  const authorNm = userInfo?.authorNm || userInfo?.author_nm || '';
  const authorCd = String(userInfo?.authorCd || userInfo?.author_cd || '').toUpperCase();
  const userNm = userInfo?.userNm || userInfo?.user_nm || '';
  const dirKind = String(userInfo?.directoryEntryKind || userInfo?.directory_entry_kind || '').toUpperCase();
  const isOrgProfile =
    dirKind === 'ORG' || (dirKind === '' && (authorCd === 'COMPANY' || authorCd === 'ORGANIZATION'));

  const typeDisplay =
    (Array.isArray(memberTypeDefinitions) && memberTypeDefinitions.length > 0
      ? displayNmForAuthorCd(memberTypeDefinitions, authorCd, opts.lang)
      : '') ||
    authorNm ||
    authorCd ||
    '-';
  rows.push({ key: profileRowLabels.TYPE, value: typeDisplay });

  if (!isOrgProfile) {
    rows.push({ key: profileRowLabels.NAME, value: userNm || '-' });
    rows.push({ key: profileRowLabels.EMAIL, value: userInfo?.userEmail || userInfo?.user_email || '-' });
    rows.push({ key: profileRowLabels.PHONE, value: userInfo?.userTel || userInfo?.user_tel || '-' });
    rows.push({
      key: resolveLabelForFieldKey('affiliation', L, t) || profileRowLabels.AFFILIATION,
      value: pickDetailsString(formData, ['affiliation']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('school_name', L, t) || profileRowLabels.SCHOOL,
      value: pickDetailsString(formData, ['school_name']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('major', L, t) || profileRowLabels.MAJOR,
      value: pickDetailsString(formData, ['major']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('interests', L, t) || profileRowLabels.INTERESTS,
      value: pickDetailsString(formData, ['interests']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('certifications', L, t) || profileRowLabels.CERTIFICATIONS,
      value: pickDetailsString(formData, ['certifications']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('major_projects_papers_achievements', L, t) || profileRowLabels.MAJOR_PROJECTS,
      value: pickDetailsString(formData, ['major projects papers achievements', 'major_projects_papers_achievements']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('preferences_fields', L, t) || profileRowLabels.PREFERENCES_FIELDS,
      value: pickDetailsString(formData, ['preferences_fields']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('preferences_org_types', L, t) || profileRowLabels.PREFERENCES_ORG_TYPES,
      value: pickDetailsString(formData, ['preferences_org_types']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('preferences_location', L, t) || profileRowLabels.PREFERENCES_LOCATION,
      value: pickDetailsString(formData, ['preferences_location']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('preferences_work_type', L, t) || profileRowLabels.PREFERENCES_WORK_TYPE,
      value: pickDetailsString(formData, ['preferences_work_type']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('bio', L, t) || profileRowLabels.BIO,
      value: pickDetailsString(formData, ['bio']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('sns', L, t) || profileRowLabels.SNS,
      value: pickDetailsString(formData, ['sns']) || '-',
    });
  } else {
    const orgNm =
      pickDetailsString(formData, ['company_name', 'companyName', 'organization_name', 'org_name']) || userNm;
    rows.push({
      key: resolveLabelForFieldKey('organization_name', L, t) || profileRowLabels.ORG_NAME,
      value: orgNm || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('main_industry', L, t) || profileRowLabels.MAIN_INDUSTRY,
      value: pickDetailsString(formData, ['main_industry']) || '-',
    });
    rows.push({
      key: resolveLabelForFieldKey('technical_field', L, t) || profileRowLabels.TECHNICAL_FIELD,
      value: pickDetailsString(formData, ['technical_field']) || '-',
    });
    rows.push({ key: profileRowLabels.CONTACT_PHONE, value: userInfo?.userTel || userInfo?.user_tel || '-' });
    rows.push({ key: profileRowLabels.EMAIL, value: userInfo?.userEmail || userInfo?.user_email || '-' });
  }

  return rows.filter((r) => r.value != null && String(r.value).trim() !== '');
}
